import {
	JSX,
	SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";

import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";

import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";

import { isEmailValid } from "utils/Utils";
import useFetch from "utils/fetch/useFetch";

import { usePrincipal } from "authentication/AuthProvider";
import OrganisationUserClient from "organisation/user/OrganisationUserClient";

export interface UserRightInsertRowProps {
	existingUsers: string[];
	roles: string[];

	onInsert: (user: string, roles: string[]) => void;
}

export default function UserRightInsertRow(
	props: Readonly<UserRightInsertRowProps>
): JSX.Element {
	const principal = usePrincipal();
	const { call } = useFetch();

	const [organisationUsers, setOrganisationUsers] = useState<string[]>([]);
	const availableUsers = useMemo(
		() =>
			organisationUsers.filter(
				user => !props.existingUsers.includes(user)
			),
		[props.existingUsers, organisationUsers]
	);

	const [user, setUser] = useState("");
	const [userValid, setUserValid] = useState(true);

	const [roles, setRoles] = useState<string[]>([]);

	useEffect(() => {
		if (
			!principal ||
			!principal.isPrivateOrganisation() ||
			!principal.organisationId
		) {
			setOrganisationUsers([]);
			return;
		}

		call(OrganisationUserClient.getAll(principal.organisationId)).then(
			response => setOrganisationUsers(response.map(user => user.email))
		);
	}, [principal]);

	function isValid(): boolean {
		return user !== "" && userValid;
	}

	const onClear = useCallback((): void => {
		setUser("");
		setUserValid(true);
		setRoles([]);
	}, []);

	function isUserValid(): boolean {
		return isEmailValid(user) && !props.existingUsers.includes(user);
	}

	const onInsert = useCallback((): void => {
		const validUser = isUserValid();
		setUserValid(validUser);

		if (validUser) {
			props.onInsert(user.toLowerCase(), roles);
			onClear();
		}
	}, [props.onInsert, user, roles]);

	const updateRoles = useCallback(
		(event: SelectChangeEvent<string[]>): void => {
			const value = event.target.value;
			if (typeof value === "string") {
				setRoles(value.split(","));
			} else {
				setRoles(value);
			}
		},
		[]
	);

	const updateUser = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			setUser(event.target.value);
			setUserValid(true);
		},
		[]
	);

	const handleSelect = useCallback(
		(_: SyntheticEvent, value: string | null): void => {
			if (value !== null) {
				setUser(value);
				setUserValid(true);
			} else {
				setUser("");
				setUserValid(false);
			}
		},
		[setUser, setUserValid]
	);

	return (
		<TableRow>
			<TableCell component="th" scope="row">
				{availableUsers.length > 0 ? (
					<Autocomplete
						clearOnBlur={false}
						selectOnFocus={false}
						options={availableUsers}
						onChange={handleSelect}
						value={user}
						renderInput={params => (
							<TextField
								{...params}
								fullWidth
								error={!userValid}
								variant="standard"
								onChange={updateUser}
								inputProps={{
									...params?.inputProps,
									"aria-label": "user"
								}}
							/>
						)}
					/>
				) : (
					<TextField
						required
						fullWidth
						value={user}
						error={!userValid}
						onChange={updateUser}
						variant="standard"
					/>
				)}
			</TableCell>
			<TableCell>
				<Select
					variant="standard"
					fullWidth
					multiple
					value={roles}
					onChange={updateRoles}
					SelectDisplayProps={{ "aria-label": "role" }}>
					{props.roles.map(role => (
						<MenuItem key={role} value={role}>
							{role}
						</MenuItem>
					))}
				</Select>
			</TableCell>
			<TableCell padding="none" align="right">
				<IconButton
					aria-label="create"
					disabled={!isValid()}
					size="small"
					onClick={onInsert}>
					<CheckIcon fontSize="inherit"></CheckIcon>
				</IconButton>
				<IconButton
					aria-label="clear"
					disabled={user === "" && roles.length === 0}
					size="small"
					onClick={onClear}>
					<ClearIcon fontSize="inherit"></ClearIcon>
				</IconButton>
			</TableCell>
		</TableRow>
	);
}
